import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  ClipboardCheck,
  Cog,
  Filter,
  Flame,
  Layers,
  Package,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Thermometer,
  Warehouse,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { FieldWorkContext } from "@/modules/mes/components/FieldWorkContextPanel";
import { mesOpActionLabel } from "@/modules/mes/components/mesOpLabels";
import { MesOpStatusBadge } from "@/modules/mes/components/MesOpStatusBadge";

export type FieldTaskCardProps = {
  to: string;
  status: string;
  workContext: FieldWorkContext;
  /** Optional caption above verb (e.g. activity timestamp). */
  meta?: string;
};

/**
 * Ánh xạ icon thực tế chuẩn công nghiệp theo từng công đoạn sản xuất thay vì dùng icon media player (Play/Skip).
 */
function resolveOpIcon(
  opName?: string | null,
  opCode?: string | null,
  status?: string,
): LucideIcon {
  if (status === "Blocked") return AlertTriangle;
  const name = (opName ?? "").toLowerCase();
  const code = (opCode ?? "").toLowerCase();
  if (name.includes("tiếp nhận") || code.includes("recv")) return PackageCheck;
  if (name.includes("nấu") || code.includes("cook")) return Flame;
  if (name.includes("kho") || code.includes("fg-gr") || code.includes("gr")) return Warehouse;
  if (name.includes("sơ chế") || code.includes("prep")) return Layers;
  if (name.includes("xay") || name.includes("nghiền") || code.includes("mill")) return Cog;
  if (name.includes("lọc") || code.includes("filter")) return Filter;
  if (name.includes("phối") || name.includes("trộn") || code.includes("blend") || code.includes("mix")) return RefreshCw;
  if (name.includes("chiết") || name.includes("đóng") || code.includes("pack")) return Package;
  if (name.includes("nguội") || code.includes("cool")) return Thermometer;
  if (name.includes("bảo quản")) return ShieldCheck;
  if (name.includes("kiểm tra") || code.includes("qc") || code.includes("iqc")) return ClipboardCheck;
  return Wrench;
}

/**
 * Queue card — Compact Mobile Card:
 * Tối ưu hóa theo hàng ngang, phân tầng rõ ràng (Tiêu đề + Trạng thái -> Sản phẩm -> LSX/Mẻ + Nút hành động),
 * loại bỏ việc dàn trải chiều dọc làm tốn diện tích màn hình mobile.
 */
export function FieldTaskCard({
  to,
  status,
  workContext,
  meta,
}: FieldTaskCardProps) {
  const blocked = status === "Blocked";
  const paused = status === "Paused";
  const running = status === "InProgress";
  const completed = status === "Completed";
  const action = mesOpActionLabel(status);
  const OpIcon = resolveOpIcon(workContext.operationName, workContext.operationCode, status);

  const productLine = [workContext.productName, workContext.packLabel]
    .filter(Boolean)
    .join(" · ");
  
  const orderNo = workContext.orderNo?.trim() || "";
  const rawBatch = workContext.batchLabel?.trim() || workContext.batchCode?.trim() || "";
  // Rút gọn mã mẻ nếu đã có tiền tố LSX giống orderNo để tránh lặp lại dài dòng
  const batchText =
    workContext.batchLabel?.trim() ||
    (orderNo && rawBatch.startsWith(orderNo + "-")
      ? rawBatch.slice(orderNo.length + 1)
      : rawBatch);

  return (
    <Link
      to={to}
      aria-label={[
        action,
        workContext.seq != null ? `Công đoạn ${workContext.seq}` : null,
        workContext.operationName ?? workContext.operationCode,
        orderNo ? `LSX ${orderNo}` : null,
        batchText ? `Mẻ ${batchText}` : null,
      ]
        .filter(Boolean)
        .join(" · ")}
      className={cn(
        "group flex flex-col gap-1.5 rounded-2xl border border-border/70 bg-surface p-3 shadow-xs",
        "transition-all duration-150 hover:shadow-sm hover:border-primary/40 active:scale-[0.99]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
        "no-underline",
        blocked
          ? "border-l-[3px] border-l-rose-500"
          : paused
            ? "border-l-[3px] border-l-amber-500"
            : running
              ? "border-l-[3px] border-l-blue-500"
              : completed
                ? "border-l-[3px] border-l-emerald-600"
                : "",
      )}
    >
      {/* Hàng 1: Tiêu đề công đoạn + Badge trạng thái */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <OpIcon
            className={cn(
              "size-4.5 shrink-0",
              running
                ? "text-blue-600"
                : blocked
                  ? "text-rose-600"
                  : paused
                    ? "text-amber-600"
                    : completed
                      ? "text-emerald-600"
                      : "text-text-muted",
            )}
            strokeWidth={2}
            aria-hidden
          />
          <h3 className="m-0 truncate text-[14px] font-bold leading-tight text-text">
            {workContext.seq != null ? `${workContext.seq}. ` : ""}
            {workContext.operationName ?? workContext.operationCode}
          </h3>
        </div>
        <div className="shrink-0">
          <MesOpStatusBadge status={status} />
        </div>
      </div>

      {/* Hàng 2: Sản phẩm + Quy cách + Mã công đoạn */}
      <div className="flex items-center gap-1.5 text-[12px] text-text-secondary leading-snug pl-6.5">
        {productLine ? (
          <span className="truncate font-medium">{productLine}</span>
        ) : null}
        {workContext.operationCode && workContext.operationName ? (
          <span className="font-mono text-text-muted shrink-0 text-[11px] before:content-['·'] before:mr-1.5">
            {workContext.operationCode}
          </span>
        ) : null}
      </div>

      {meta ? (
        <p className="m-0 truncate text-[11px] text-text-muted pl-6.5">{meta}</p>
      ) : null}

      {/* Hàng 3: LSX, Mẻ + Nút hành động */}
      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-border/50 pl-6.5">
        <div className="flex items-center gap-2 min-w-0 text-[11px] font-mono">
          {orderNo ? (
            <span className="truncate">
              <span className="text-text-muted">LSX:</span>{" "}
              <span className="font-semibold text-text">{orderNo}</span>
            </span>
          ) : null}
          {batchText ? (
            <span className="truncate">
              <span className="text-text-muted">Mẻ:</span>{" "}
              <span className="font-semibold text-text">{batchText}</span>
            </span>
          ) : null}
        </div>

        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-all ml-auto",
            "bg-primary text-on-primary shadow-2xs group-hover:bg-primary-hover active:opacity-90",
          )}
        >
          <span>{action}</span>
          <ChevronRight
            className="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
            aria-hidden
          />
        </span>
      </div>
    </Link>
  );
}
